import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@splitfiesta/core';
import {
  type PasskeyCredential,
  clearPasskeyEmail,
  deletePasskey,
  getPasskeyEmail,
  getTokens,
  isPasskeySupported,
  listPasskeys,
  registerPasskey,
  savePasskeyEmail,
  signInWithPasskey,
} from '@splitfiesta/platform-web/auth/cognito';

/**
 * The query persister stores `createdAt` as an ISO string, so a rehydrated
 * list must be revived before anything formats the date.
 */
export function revivePasskeyDates(
  passkeys: PasskeyCredential[]
): PasskeyCredential[] {
  return passkeys.map(passkey =>
    passkey.createdAt instanceof Date
      ? passkey
      : {
          ...passkey,
          createdAt: new Date(passkey.createdAt as unknown as string),
        }
  );
}

/**
 * Query to fetch user's registered passkeys
 */
export function usePasskeysQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.userPasskeys(),
    queryFn: async (): Promise<PasskeyCredential[]> => {
      const tokens = await getTokens();
      if (!tokens?.accessToken) {
        throw new Error('Not authenticated');
      }
      return listPasskeys(tokens.accessToken);
    },
    select: revivePasskeyDates,
    enabled: enabled && isPasskeySupported(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Mutation to register a new passkey
 * @param userEmail - The user's email to save for usernameless sign-in
 */
export function useRegisterPasskeyMutation(userEmail?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const tokens = await getTokens();
      if (!tokens?.accessToken) {
        throw new Error('Not authenticated');
      }
      return registerPasskey(tokens.accessToken);
    },
    onSuccess: () => {
      // Save email for usernameless passkey sign-in
      if (userEmail) {
        savePasskeyEmail(userEmail);
      }
      // Invalidate passkeys query to refetch the list
      queryClient.invalidateQueries({ queryKey: queryKeys.userPasskeys() });
    },
  });
}

/**
 * Mutation to delete a passkey
 * Clears stored email if no passkeys remain after deletion
 */
export function useDeletePasskeyMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentialId: string) => {
      const tokens = await getTokens();
      if (!tokens?.accessToken) {
        throw new Error('Not authenticated');
      }
      await deletePasskey(tokens.accessToken, credentialId);
      // Return tokens so we can check remaining passkeys in onSuccess
      return tokens;
    },
    onSuccess: async tokens => {
      // Invalidate passkeys query to refetch the list
      queryClient.invalidateQueries({ queryKey: queryKeys.userPasskeys() });

      // Check if user has any remaining passkeys
      try {
        const remainingPasskeys = await listPasskeys(tokens.accessToken);
        if (remainingPasskeys.length === 0) {
          // No more passkeys, clear the stored email
          clearPasskeyEmail();
        }
      } catch {
        // If we can't check, clear the email to be safe
        clearPasskeyEmail();
      }
    },
  });
}

/**
 * Mutation for passkey sign-in
 * Note: This doesn't use React Query's typical pattern since it handles
 * auth state through the auth store, similar to regular sign-in
 */
export function usePasskeySignInMutation() {
  return useMutation({
    mutationFn: async (username?: string) => {
      return signInWithPasskey(username);
    },
  });
}

/**
 * Hook to check if passkeys are supported
 */
export function usePasskeySupport() {
  return {
    isSupported: isPasskeySupported(),
  };
}

/**
 * Check if there's a stored email for passkey sign-in
 * Returns true if user has previously registered a passkey on this device
 */
export function hasStoredPasskeyEmail(): boolean {
  return getPasskeyEmail() !== null;
}
